import { Component, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { InputComponent } from '../../../shared/input/input.component';
import { ButtonComponent } from '../../../shared/button/button.component';
import { MatStepperModule } from '@angular/material/stepper';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';
import { SignupService } from '../../../services/signup/signup.service';
import * as signupConstants from '../../../shared/constants/signupConst';
import { ModalDialogComponent } from '../../../shared/modal-dialog/modal-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { IconsComponent } from '../../../shared/icons/icons.component';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { max } from 'rxjs';

export function textOnlyValidator(control: FormControl): { [key: string]: any } | null {
  const value = control.value;

  if (!value || typeof value !== 'string') {
    return null;
  }

  const hasNumber = /\d/.test(value);
  const hasExtraSpaces = !/^\S+( \S+)*$/.test(value);
  const hasSpecialChars = /[^a-zA-Z\s]/.test(value); // disallow anything other than letters and spaces

  if (hasNumber) {
    return { containsNumber: true };
  }

  if (hasSpecialChars) {
    return { containsSpecialChars: true };
  }

  if (hasExtraSpaces) {
    return { textOnly: true };
  }

  return null;
}

// Function to validate password based on all criteria
function validatePassword(control: FormControl): { [key: string]: any } | null {
  const pswd = control.value;
  const errors: { [key: string]: boolean } = {}; // Declare the type of errors

  // Check password length
  if (pswd.length < 8 || pswd.length > 32) {
    errors['passwordLength'] = true;
  }

  // Check for uppercase character
  let hasUpper = false;
  for (let i = 0; i < pswd.length; i++) {
    if (pswd.charCodeAt(i) >= 65 && pswd.charCodeAt(i) <= 90) {
      hasUpper = true;
      break;
    }
  }
  if (!hasUpper) {
    errors['uppercase'] = true;
  }

  // Check for number
  let hasNumber = false;
  for (let i = 0; i < pswd.length; i++) {
    if (pswd.charCodeAt(i) >= 48 && pswd.charCodeAt(i) <= 57) {
      hasNumber = true;
      break;
    }
  }
  if (!hasNumber) {
    errors['number'] = true;
  }

  // Check for special character
  let hasSpecialChar = false;
  for (let i = 0; i < pswd.length; i++) {
    if (
      (pswd.charCodeAt(i) >= 33 && pswd.charCodeAt(i) <= 47) ||
      (pswd.charCodeAt(i) >= 58 && pswd.charCodeAt(i) <= 64) ||
      (pswd.charCodeAt(i) >= 91 && pswd.charCodeAt(i) <= 96) ||
      (pswd.charCodeAt(i) >= 123 && pswd.charCodeAt(i) <= 126)
    ) {
      hasSpecialChar = true;
      break;
    }
  }
  if (!hasSpecialChar) {
    errors['specialChar'] = true;
  }
  return Object.keys(errors).length > 0 ? errors : null;
}

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    RouterModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    CommonModule,
    InputComponent,
    ButtonComponent,
    MatStepperModule,
    NgOtpInputModule,
    IconsComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.css',
})
export class SignupComponent {
  @ViewChild(NgOtpInputComponent) otpInput!: NgOtpInputComponent;

  isLinear = true;
  firstForm!: FormGroup;
  secondForm!: FormGroup;
  thirdForm!: FormGroup;
  combinedForm!: FormGroup;

  signupForm!: FormGroup;
  hide = signupConstants.HIDE;
  private typingTimeout: any;
  private lastOtp: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private signUpService: SignupService,
    public dialog: MatDialog,
    private messageModal: MessageModalService
  ) {}

  textInputType = signupConstants.TEXT_INPUT_TYPE;
  emailInputType = signupConstants.EMAIL_INPUT_TYPE;
  passwordInputType = signupConstants.PASSWORD_INPUT_TYPE;
  otpInputType = signupConstants.OTP_INPUT_TYPE;

  newPasswordVisibility = signupConstants.NEW_PASSWORD_VISIBILITY;
  confirmPasswordVisibilty = signupConstants.CONFIRM_PASSWORD_VISIBILITY;
  visibilityOffIcon = signupConstants.VISIBILITY_OFF_ICON;
  visibilityIcon = signupConstants.VISIBILITY_ICON;
  lockIcon = signupConstants.LOCK_ICON;
  prefixInputIcon = signupConstants.PREFIX_INPUT_ICON;
  mailIcon = signupConstants.MAIL_ICON;
  accountCircleIcon = signupConstants.ACCOUNT_CIRCLE_ICON;
  phoneIcon = signupConstants.PHONE_ICON;

  fnameLabel = signupConstants.FNAME_LABEL;
  lnameLabel = signupConstants.LNAME_LABEL;
  emailLabel = signupConstants.EMAIL_LABEL;
  phnLabel = signupConstants.PHN_LABEL;
  orgnameLabel = signupConstants.ORGNAME_LABEL;
  passwordLabel_new = signupConstants.PASSWORD_LABEL_NEW;
  passwordLabel_confirm = signupConstants.PASSWORD_LABEL_CONFIRM;

  fnamePlaceHolder = signupConstants.FNAME_PLACEHOLDER;
  lnamePlaceHolder = signupConstants.LNAME_PLACEHOLDER;
  emailPlaceholder = signupConstants.EMAIL_PLACEHOLDER;
  phnPlaceholder = signupConstants.PHN_PLACEHOLDER;
  orgNamePlaceholder = signupConstants.ORGNAME_PLACEHOLDER;
  phnStyle = signupConstants.PHN_STYLE;
  resendCode = signupConstants.RESEND_CODE;

  nextbtn = signupConstants.NEXT_BTN;
  backLabel = signupConstants.BACK_LABEL;
  criteria1 = signupConstants.CRITERIA_1;
  criteria2 = signupConstants.CRITERIA_2;
  criteria3 = signupConstants.CRITERIA_3;
  criteria4 = signupConstants.CRITERIA_4;

  fname = new FormControl('', [Validators.required, textOnlyValidator, Validators.maxLength(256)]);
  lname = new FormControl('', [Validators.required, textOnlyValidator, Validators.maxLength(256)]);
  emailMat = new FormControl('', [Validators.required, Validators.email]);
  phone = new FormControl('', [Validators.required, Validators.pattern(/^[+()\-0-9]+$/)]);
  newPswd = new FormControl('', [Validators.required, validatePassword]);
  confirmPswd = new FormControl('', [Validators.required, validatePassword]);
  orgName = new FormControl({ value: '', disabled: true });
  isSecondFormValid = false;

  //buttons

  buttonone = signupConstants.BUTTON_ONE;
  buttontwo = signupConstants.BUTTON_TWO;
  buttonthree = signupConstants.BUTTON_THREE;
  submitDisabled = signupConstants.SUBMIT_DISABLED;
  icon = signupConstants.CHEVRON_ICON;
  ariaHidden = signupConstants.ARIA_HIDDEN;
  ariaLabel = signupConstants.ARIA_LABEL;
  otpError: boolean = signupConstants.OTP_ERROR;
  orgError: boolean = signupConstants.ORG_ERROR;

  handleChange() {
    this.isSecondFormValid =
      this.validateSecondFormPasswordMatch() &&
      validatePassword(this.newPswd) === null &&
      validatePassword(this.confirmPswd) === null;
  }
  handleGotoLogin() {
    this.router.navigate(['/login']);
  }

  validateSecondFormPasswordMatch() {
    if (
      this.newPswd?.value?.length &&
      this.confirmPswd?.value?.length &&
      this.newPswd?.value === this.confirmPswd?.value
    ) {
      return true;
    }
    return false;
  }

  handleNewPswdVisibilityCallback(event: any) {
    this.newPasswordVisibility = !this.newPasswordVisibility;
  }
  handleConfirmPswdVisibilityCallback(event: any) {
    this.confirmPasswordVisibilty = !this.confirmPasswordVisibilty;
  }

  onOtpChange(event: string) {
    if (event.length === 6) {
      if (this.emailMat.value) {
        // Guard against re-validating a code we have already sent. ng-otp-input re-emits the
        // full value on any keyup over a filled box (Enter, Tab, the key release after a paste),
        // and the code is single-use, so the duplicate request comes back 400 and fails against
        // a code the backend has already accepted.
        if (this.lastOtp === event) {
          return;
        }
        this.lastOtp = event;
        this.signUpService.validateOtp(this.emailMat.value, event).subscribe({
          next: (response) => {
            if (response.success) {
              this.submitDisabled = false;
              this.otpError = false;
            } else {
              this.submitDisabled = true;
              this.otpError = true;
            }
          },
          error: (err) => {
            console.error(err);
            this.submitDisabled = true;
            this.otpError = true;
          },
        });
      }
    } else {
      // Partial code: clear the guard so re-entering the same digits validates again.
      this.lastOtp = '';
      this.submitDisabled = true;
      this.otpError = false;
    }
  }

  otpCode: string = '';
  resendOtp() {
    this.otpInput.setValue('');
    this.otpError = false;
    this.lastOtp = '';
    this.fetchOtp();
  }
  fetchOtp() {
    if (this.emailMat.value) {
      this.signUpService.fetchOtp(this.emailMat.value).subscribe({
        next: (response) => {
          this.otpCode = response.code;
        },
        error: (err) => {
          console.error(err);
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching the otp.`);
        },
      });
    }
  }

  //checkboxes

  hasUpperCase(pswd: string) {
    return /[A-Z]/.test(pswd);
  }

  hasNumber(pswd: string) {
    return /\d/.test(pswd);
  }

  hasSplChar(pswd: string) {
    return /[^a-zA-Z0-9]/.test(pswd);
  }

  checkCriteria() {
    const pswd = this.newPswd.value;
    const pswdConfirm = this.confirmPswd.value;
    if (
      pswd &&
      pswdConfirm &&
      (pswd.length < 8 || pswd.length > 32) &&
      this.hasUpperCase(pswd) &&
      this.hasNumber(pswd) &&
      this.hasSplChar(pswd) &&
      pswd === pswdConfirm
    ) {
      return true;
    } else {
      return false;
    }
  }

  ngOnInit(): void {
    this.firstForm = this.fb.group({
      fname: this.fname,
      lname: this.lname,
      emailMat: this.emailMat,
      phone: this.phone,
    });
    this.secondForm = this.fb.group({
      newPswd: this.newPswd,
      confirmPswd: this.confirmPswd,
    });
    this.thirdForm = this.fb.group({
      thirdCtrl: ['', Validators.required],
    });

    this.combinedForm = this.fb.group({
      firstForm: this.firstForm,
      secondForm: this.secondForm,
      thirdForm: this.thirdForm,
    });
  }
  handleSubmitForm(event: any) {
    if (this.emailMat.value && this.newPswd.value && this.phone.value) {
      this.signUpService
        .signup(`${this.fname.value} ${this.lname.value}`, this.emailMat.value, this.newPswd.value, this.phone.value)
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.userCreatedFunction();
            } else {
              this.messageModal.openModal($localize`Error`, $localize`${response.message}`);
            }
            this.router.navigate(['/login']);
          },
          error: (err) => {
            console.error(err);
            this.messageModal.openModal($localize`Error`, this.userCreationFailedTitle);
          },
        });
    }
  }

  handleGetOrgName(event: any) {
    const email = this.emailMat.value;
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    this.typingTimeout = setTimeout(() => {
      if (email) {
        if (this.isValidEmail(email)) {
          this.signUpService.getOrgName(email).subscribe({
            next: (response) => {
              this.orgName.setValue(response.data.Name);
              this.orgError = false;
            },
            error: (err) => {
              console.error(err);
              this.orgName.setValue('');
              this.orgError = true;
            },
          });
        }
      } else {
        this.orgName.setValue('');
      }
    }, 2000);
  }

  popupTitle = signupConstants.POPUP_TITLE;
  popupOK = signupConstants.POPUP_OK;
  popupCancel = signupConstants.POPUP_CANCEL;
  userCreationFailedTitle = signupConstants.USER_CREATION_FAILED;

  userCreatedFunction() {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '400px',
      data: {
        title: this.popupTitle,
        primaryButtonLabel: this.popupOK,
        showPrimaryButton: true,
      },
    });
    dialogRef.componentInstance.primaryAction.subscribe(() => {
      this.router.navigate(['/login']);
      dialogRef.close(true);
    });
  }

  isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }
}
